import {
  EventsOnboardPayload,
  ShareholderEventType,
  ShareClass,
  Entity,
  ShareholderEvent,
  ShareholderRecord,
  TransactionEvent,
  EventDataSubType,
  EmissionEvent,
  EstablishEvent,
  ImportEvent,
  SplitEvent,
  RoundingStrategy,
  SpliceEvent,
  ShareDeletionDataEvent,
  DividendEvent,
  EventDataemissionSubtype,
  EventDataDividendSubtype,
  ShareParvalueChangeEvent,
} from '../../classes/shared';
import { defaultShareClass } from '../generic-data';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { Events } from '../../model/events';
import { textMappings, texts } from '@orgbrain/lib-data';
import { Entities } from '../../model/entity';
import { validDateFormats } from '../valid-dates';

export enum EventsImportErrorCodes {
  NO_VALID_HEADERS_FOUND = 'NO_VALID_HEADERS_FOUND',
  NO_DATA_AFTER_HEADER = 'NO_DATA_AFTER_HEADER',
  NOT_A_VALID_DATE = 'NOT_A_VALID_DATE',
  EVENTS_INCONSISTENT = 'EVENTS_INCONSISTENT',
  NOT_VALID_TRANSACTION = 'NOT_VALID_TRANSACTION',
}

export interface EventsImportError extends Error {
  code: EventsImportErrorCodes;
  message: string;
}

export enum EventsImportWarningCodes {
  DUBBLE_ORGANIZATION_NUMBER = 'DUBBLE_ORGANIZATION_NUMBER',
  MINUS_SHARES_IN_REGISTERY = 'MINUS_SHARES_IN_REGISTERY',
}

export interface EventsImportWarning extends Error {
  code: EventsImportWarningCodes;
  message: string;
}

export enum ValidHeadersEnum {
  'type' = 'type',
  'date' = 'date',
  'buyer' = 'buyer',
  'buyeremail' = 'buyeremail',
  'buyerinvestorid' = 'buyerinvestorid',
  'buyercontactpersoncompany' = 'buyercontactpersoncompany',
  'seller' = 'seller',
  'selleremail' = 'selleremail',
  'sellerinvestorid' = 'sellerinvestorid',
  'amount' = 'amount',
  'price' = 'price',
  'shareclass' = 'shareclass',
  'notes' = 'notes',
  'buyeraddress' = 'buyeraddress',
  'buyerzip' = 'buyerzip',
  'buyercity' = 'buyercity',
  'buyertlf' = 'buyertlf',
  'buyercountry' = 'buyercountry',
  'buyervps' = 'buyervps',
  'buyerlei' = 'buyerlei',
  'selleraddress' = 'selleraddress',
  'sellerzip' = 'sellerzip',
  'sellercity' = 'sellercity',
  'sellertlf' = 'sellertlf',
  'sellercountry' = 'sellercountry',
  'sellervps' = 'sellervps',
  'sellerlei' = 'sellerlei',
  'from' = 'from',
  'to' = 'to',
  'strategy' = 'strategy',
  'dividendpaymentdate' = 'dividendpaymentdate',
  'accounttype' = 'accounttype',
}
export enum ErrorCodes {
  'EMPTYROW' = 'EMPTYROW',
  'NOTAEMAIL' = 'NOTAEMAIL',
  'NOTANUMBER' = 'NOTANUMBER,',
  'NOTDEFINED' = 'NOTDEFINED',
  'NOTALLOWEDFORENTITY' = 'NOTALLOWEDFORENTITY',
}
export const translatedHeader = {
  type: ['type'],
  date: ['dato', 'date'],
  buyer: ['kjøper', 'buyer'],
  buyeremail: ['epostkjøper', 'buyeremail'],
  buyerinvestorid: [
    'fødselsnummerorgnrkjøper',
    'investoridkjøper',
    'investoridbuyer',
    'socialsecurityorgnrbuyer',
  ],
  buyeraddress: ['adressekjøper', 'buyeraddress'],
  buyerzip: ['postnrkjøper', 'buyerzip'],
  buyercity: ['bykjøper', 'buyercity', 'poststedkjøper'],
  buyertlf: ['telefonnummerkjøper', 'buyertelephone'],
  buyercontactpersoncompany: [
    'buyercontactpersoncompany',
    'kjøperkontaktpersonselskap',
    'kjøperrepresentantforselskap',
    'kjøperkontaktpersonforvirksomhet',
    'kontaktpersonforvirksomhet',
  ],
  buyercountry: [
    'Statsborgerskapkjøper',
    'kjøperland',
    'landkjøper',
    'buyercountry',
    'buyerland',
    'buyercountry',
  ],

  buyervps: ['vpsbuyer', 'vpskjøper', 'vpsnummerkjøper', 'vpsnumberbuyer'],
  buyerlei: ['leibuyer', 'leikjøper', 'leinummerkjøper', 'leinumberbuyer'],
  seller: ['selger', 'seller'],
  selleremail: ['epostselger', 'selleremail'],
  sellerinvestorid: [
    'fødselsnummerorgnrselger',
    'investoridselger',
    'investoridseller',
    'socialsecurityorgnrseller',
  ],
  selleraddress: ['adresseselger', 'selleraddress'],
  sellerzip: ['postnrselger', 'sellerzip'],
  sellercity: ['byselger', 'sellercity', 'poststedselger'],
  sellertlf: ['telefonnummerselger', 'sellertelephone'],
  sellercountry: [
    'Statsborgerskapselger',
    'sellercountry',
    'selgerland',
    'sellerland',
    'landselger',
  ],

  sellervps: ['vpsseller', 'vpsselger', 'vpsnummerselger', 'vpsnumberseller'],
  sellerlei: ['leiseller', 'leiselger', 'leinummerselger', 'leinumberseller'],
  amount: ['antall', 'shares', 'amount'],
  price: ['pris', 'price', 'kurs'],
  shareclass: ['aksjeklasse', 'shareclass'],
  notes: ['aksjenummer', 'notes', 'sharenumbers'],
  from: ['fra', 'from'],
  to: ['til', 'to'],
  strategy: ['strategi', 'strategy'],
  dividendpaymentdate: ['dividendpaymentdate', 'utbyttebetalingsdato'],
  accounttype: ['accounttype', 'kontotype'],
};

export const standardHeader = {
  type: 'Type',
  date: 'Dato',
  buyer: 'Kjøper',
  buyeremail: 'E-post kjøper',
  buyerinvestorid: 'Investorid kjøper',
  buyercontactpersoncompany: 'Kontaktperson for virksomhet',
  seller: 'Selger',
  selleremail: 'E-post selger',
  sellerinvestorid: 'Investorid selger',
  amount: 'Antall',
  price: 'Pris',
  shareclass: 'Aksjeklasse',
  notes: 'Aksjenummer',
  buyeraddress: 'Adresse kjøper',
  buyerzip: 'Postnr kjøper',
  buyercity: 'Poststed kjøper',
  buyertlf: 'Telefonnummer kjøper',
  buyercountry: 'Land kjøper',
  buyervps: 'VPS-nummer kjøper',
  buyerlei: 'LEI-nummer kjøper',
  selleraddress: 'Adresse selger',
  sellerzip: 'Postnr selger',
  sellercity: 'Poststed selger',
  sellertlf: 'Telefonnummer selger',
  sellercountry: 'Land selger',
  sellervps: 'VPS-nummer selger',
  sellerlei: 'LEI-nummer selger',
  from: 'Fra',
  to: 'Til',
  strategi: 'Strategi',
  dividendpaymentdate: 'Utbytte betalingsdato',
};

export const specialTransactions = Object.keys(EventDataSubType).reduce(
  (acc: any, subtype: any /*EventDataSubType*/): any => {
    const norwegianKey = texts[subtype]['default']['nb']
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    const englishKey = texts[subtype]['default']['en']
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    acc[subtype] = [norwegianKey, englishKey];
    return acc;
  },
  {}
);
export const specialEmissions = Object.keys(EventDataemissionSubtype).reduce(
  (acc: any, subtype: any /*EventDataSubType*/): any => {
    const norwegianKey = texts[subtype]['default']['nb']
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    const englishKey = texts[subtype]['default']['en']
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    acc[subtype] = [norwegianKey, englishKey];
    return acc;
  },
  {}
);
export const specialDividend = Object.keys(EventDataDividendSubtype).reduce(
  (acc: any, subtype: any /*EventDataSubType*/): any => {
    const tran = (texts[subtype] && texts[subtype].default) || textMappings[subtype];
    const norwegianKey = tran.nb
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    const englishKey = tran.en
      .toLowerCase()
      .trim()
      .replace(/ /g, '')
      .replace(/-/g, '')
      .replace(/\//g, '');
    acc[subtype] = [norwegianKey, englishKey];
    return acc;
  },
  {}
);

const translatedType = {
  emission: ['emisjon', 'shareissue', 'emission'],
  establish: ['stiftelse', 'establish'],
  import: ['øyeblikksbilde', 'snapshot'],
  split: ['splitt', 'split'],
  splice: ['spleis', 'splice'],
  sharedelete: ['slett', 'delete', 'sharedelete'],
  dividend: ['dividend', 'utbytte', 'utbytteutbetaling'],
  parvaluechange: ['parvaluechange', 'endrepålydende', 'endringavpålydende'],
  ...specialTransactions,
};

export const translatedTypeCapitalized = Object.keys(translatedType).reduce(
  (acc: any, key: string) => {
    acc[key] = capitalizeFirstLetter(translatedType[key][0]);
    return acc;
  },
  {}
);
function capitalizeFirstLetter(str: string): string {
  const first = str[0].toUpperCase();
  const rest = str.slice(1);
  return [first, rest].join('');
}

const translatedRoundingStrategies = {
  ned: RoundingStrategy.DOWN,
  down: RoundingStrategy.DOWN,
  opp: RoundingStrategy.UP,
  up: RoundingStrategy.UP,
};

const validHeaders = Object.keys(ValidHeadersEnum);

class GenericEventRow {
  type: ShareholderEventType;
  date: string;
  buyer: string;
  buyeremail?: string;
  buyerinvestorid?: string;
  dob?: string;
  pnr?: string;
  buyerId?: string;
  orgcontactname?: string;
  buyeraddress?: string;
  buyerzip?: string;
  buyercity?: string;
  buyertlf?: string;
  buyervps?: string;
  buyerlei?: string;
  contactName?: string;
  buyercontactpersoncompany?: string;
  buyercountry?: string;
  seller?: string;
  selleremail?: string;
  sellerinvestorid?: string;
  sellerId?: string;
  selleraddress?: string;
  sellerzip?: string;
  sellercity?: string;
  sellertlf?: string;
  sellercountry?: string;
  sellercontactpersoncompany?: string;
  sellervps?: string;
  sellerlei?: string;
  amount: number;
  price?: number;
  shareclass?: string;
  shareclassId?: string;
  notes?: string;
  accounttype?: string;
}

export class EventsImportPipeline {
  public static convertTableToEventsOnboardPayload(
    data: string[][],
    org: any,
    existingEntities: Entity[],
    existingShareclasses: ShareClass[]
  ): EventsOnboardPayload {
    const genericEvents: GenericEventRow[] = this.eventsTableToGeneric(data);

    const {
      entities,
      eventDataWithEntityIds,
    }: { entities: Entity[]; eventDataWithEntityIds: GenericEventRow[] } = this.parseEntities({
      eventData: genericEvents,
      existingEntities,
      organizationId: org._id,
    });

    const {
      shareclasses,
      eventDataWithShareclassIds,
    }: {
      shareclasses: ShareClass[];
      eventDataWithShareclassIds: GenericEventRow[];
    } = this.parseShareclasses({ eventData: eventDataWithEntityIds, existingShareclasses, org });

    const events: ShareholderEvent[] = this.parseEvents({
      eventData: eventDataWithShareclassIds,
      organizationId: org._id,
    });

    return {
      events: events,
      entities: entities,
      shareclasses: shareclasses,
      warnings: null,
    };
  }

  public static appendPreviousEventsWithNewEvents(
    prevEvents: ShareholderEvent[],
    newEvents: ShareholderEvent[]
  ): ShareholderEvent[] {
    const firstDateNewEvents: Date = Events.sortEventsByDate(newEvents)[0].date;
    const remainingEvents = Events.sortEventsByDate(
      prevEvents
        .map(
          (event: ShareholderEvent): ShareholderEvent => ({
            ...event,
            date: new Date(event.date || event.createdAt),
          })
        )
        .filter((event: ShareholderEvent): boolean => new Date(event.date) < firstDateNewEvents)
    );
    return remainingEvents.concat(newEvents);
  }

  public static overwritingEvents(prevEvents: ShareholderEvent[], date: Date): ShareholderEvent[] {
    return prevEvents.filter(
      (event: ShareholderEvent): boolean => new Date(event.date || event.createdAt) >= date
    );
  }

  private static mapEventsHeaderToDefault(header: string): string {
    if (header) {
      const trimmedHeader: string = header
        .toLowerCase()
        .trim()
        .replace(/ /g, '')
        .replace(/-/g, '')
        .replace(/\//g, '');

      if (validHeaders.find((validHeader: string) => trimmedHeader === validHeader)) {
        return trimmedHeader;
      }
      for (const key of validHeaders) {
        if (translatedHeader[key].find((val: string) => trimmedHeader === val)) {
          return key;
        }
      }
    }
    return '';
  }

  private static headers(row: string[]): string[] {
    return row.map((header: string) => this.mapEventsHeaderToDefault(header));
  }

  private static isHeaderRow(row: string[]): boolean {
    if (row.length < 1) return false;
    const map = {};
    for (const key of row) {
      map[key] = true;
    }
    if (
      map[ValidHeadersEnum.type] &&
      map[ValidHeadersEnum.date] &&
      map[ValidHeadersEnum.amount] &&
      map[ValidHeadersEnum.buyer]
    ) {
      return true;
    }
    return false;
  }

  static eventsTableToGeneric(eventTable: string[][]): GenericEventRow[] {
    const result: GenericEventRow[] = [];
    let eventData: string[][];

    let headerIndex: number = 0;
    const ignoreIndex = [];

    for (const row of eventTable) {
      const headerRow = this.headers(row);

      if (this.isHeaderRow(headerRow)) {
        for (const collumn of row) {
          let collumnIndex = 0;
          if (collumn === '') {
            ignoreIndex.push(collumnIndex);
          }
          collumnIndex++;
        }
        eventData = eventTable.slice(headerIndex);
        eventData[0] = headerRow;
        break;
      }
      headerIndex++;
    }

    if (!eventData) {
      throw {
        code: EventsImportErrorCodes.NO_VALID_HEADERS_FOUND,
      };
    }

    if (eventData.slice(1).length === 0) {
      throw {
        code: EventsImportErrorCodes.NO_DATA_AFTER_HEADER,
      };
    }

    const headers = eventData[0];

    for (const row of eventData.slice(1)) {
      const genericEvent: GenericEventRow = {} as any;

      for (let i = 0; i < headers.length; i++) {
        genericEvent[headers[i]] = row[i] || null;
      }

      result.push(genericEvent);
    }

    this.checkFieldValues(eventTable, headers, headerIndex);

    this.CheckEventValidity(result);
    return result;
  }

  private static createShareclassMap(existingShareclasses: ShareClass[]): {
    [name: string]: ShareClass;
  } {
    const shareclassMap: { [name: string]: ShareClass } = {};

    for (const shareclass of existingShareclasses) {
      shareclassMap[shareclass.name] = shareclass;
    }
    return shareclassMap;
  }

  private static parseShareclasses({
    eventData,
    existingShareclasses,
    org,
  }: {
    eventData: GenericEventRow[];
    existingShareclasses: ShareClass[];
    org: any;
  }): { shareclasses: ShareClass[]; eventDataWithShareclassIds: GenericEventRow[] } {
    const shareclassMap: { [name: string]: ShareClass } =
      this.createShareclassMap(existingShareclasses);

    const eventDataCopy = [...eventData];
    let index = 0;
    for (const event of eventData) {
      if (event.shareclass) {
        if (!shareclassMap[event.shareclass]) {
          shareclassMap[event.shareclass] = new ShareClass({
            _id: uuid(),
            name: event.shareclass,
            organizationId: org._id,
            createMe: true,
          });
        }

        eventDataCopy[index].shareclassId = shareclassMap[event.shareclass]._id?.toString();
      } else {
        if (!shareclassMap[defaultShareClass[org.type]]) {
          shareclassMap[defaultShareClass[org.type]] = new ShareClass({
            _id: uuid(),
            name: defaultShareClass[org.type],
            organizationId: org._id,
            createMe: true,
          });
        }
        eventDataCopy[index].shareclassId =
          shareclassMap[defaultShareClass[org.type]]._id?.toString();
      }
      index++;
    }
    const shareclasses = Object.keys(shareclassMap).map((key: string) => shareclassMap[key]);

    return { shareclasses, eventDataWithShareclassIds: eventDataCopy };
  }

  private static parseName(name: string): { firstName: string; lastName: string } {
    if (name) {
      try {
        let firstName = '';
        let lastName = '';
        if (name.includes(',')) {
          let names = name.trim().split(',');
          lastName = names.slice(0, -1).join(' ');
          firstName = names[names.length - 1];
        } else {
          let names = name.trim().split(' ');
          firstName = names.slice(0, -1).join(' ');
          lastName = names[names.length - 1];
        }

        return { firstName: firstName || '', lastName: lastName || '' };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error: any) {
        return { firstName: name, lastName: '' };
      }
    } else {
      return { firstName: '', lastName: '' };
    }
  }

  private static parseDobAndPnr(investorid: string): { dob: string; pnr?: string } {
    if (!investorid) return { dob: null, pnr: null };

    let parts = investorid.split(' ');

    if (parts.length === 1) {
      const dob = parts[0];
      return { dob };
    } else if (parts.length === 2) {
      const dob = parts[0];
      const pnr = parts[1];
      return { dob, pnr };
    } else if (parts.length > 2) {
      const dob = parts[0];
      const pnr = parts.slice(1).join(' ');
      return { dob, pnr };
    } else {
      const dob = investorid;
      return { dob };
    }
  }

  private static isCompany(investorid: string): boolean {
    return investorid && (investorid.length === 9 || investorid.length === 20);
  }

  private static parseEntity({
    isCompany,
    organizationId,
    investorid,
    name,
    contactName,
    email,
    address,
    zip,
    city,
    citizenship,
    tlf,
    lei,
    vps,
    accounttype,
  }: {
    isCompany: boolean;
    organizationId: string;
    investorid: string;
    name: string;
    contactName?: string;
    email: string;
    address: string;
    zip: string;
    city: string;
    citizenship: string;
    tlf: string;
    lei: string;
    vps: string;
    accounttype?: string;
  }): Entity {
    if (isCompany) {
      const { firstName, lastName } = this.parseName(contactName);
      return {
        _id: null,
        organizationId: organizationId,
        orgnr: investorid,
        companyname: name,
        firstName: firstName,
        lastName: lastName,
        isCompany,
        email: email,
        address: address,
        zip: zip,
        city: city,
        citizenship: citizenship,
        tlf: tlf,
        createMe: true,
        lei: lei,
        vps: vps,
        accounttype: accounttype,
      } as Entity;
    } else {
      const { firstName, lastName } = this.parseName(name);
      const { dob, pnr } = this.parseDobAndPnr(investorid);
      return {
        _id: null,
        organizationId: organizationId,
        dob: dob,
        pnr: pnr,
        firstName: firstName,
        lastName: lastName,
        isCompany,
        email: email,
        address: address,
        zip: zip,
        city: city,
        citizenship: citizenship,
        tlf: tlf,
        createMe: true,
        lei: lei,
        vps: vps,
        accounttype: accounttype,
      } as Entity;
    }
  }

  private static parseEntities({
    eventData,
    existingEntities,
    organizationId,
  }: {
    eventData: GenericEventRow[];
    existingEntities: Entity[];
    organizationId: string;
  }): { entities: Entity[]; eventDataWithEntityIds: GenericEventRow[] } {
    const eventDataCopy: GenericEventRow[] = [...eventData];
    const entityMap: { [key: string]: Entity } = {};

    for (const entity of existingEntities) {
      if (entity.isCompany) {
        entityMap[entity.orgnr] = entity;
      } else {
        entityMap[Entities.getEntityEmail(entity)] = entity;
      }
    }

    let index = 0;

    for (const event of eventData) {
      const isBuyerCompany: boolean = this.isCompany(event.buyerinvestorid);
      const isSellerCompany: boolean = this.isCompany(event.sellerinvestorid);
      const buyer: Entity = this.parseEntity({
        isCompany: isBuyerCompany,
        investorid: event.buyerinvestorid,
        name: event.buyer,
        contactName: event.buyercontactpersoncompany,
        email: event.buyeremail?.toLowerCase().trim(),
        address: event.buyeraddress,
        zip: event.buyerzip,
        city: event.buyercity,
        citizenship: event.buyercountry,
        tlf: event.buyertlf,
        organizationId,
        lei: event.buyerlei,
        vps: event.buyervps,
        accounttype: event.accounttype,
      });

      const seller: Entity = this.parseEntity({
        isCompany: isSellerCompany,
        investorid: event.sellerinvestorid,
        name: event.seller,
        email: event.selleremail?.toLowerCase().trim(),
        address: event.selleraddress,
        zip: event.sellerzip,
        city: event.sellercity,
        tlf: event.sellertlf,
        citizenship: event.sellercountry,
        organizationId,
        lei: event.sellerlei,
        vps: event.sellervps,
        accounttype: event.accounttype,
      });

      if (buyer.firstName || buyer.lastName || buyer.companyname) {
        let key: string;
        if (buyer.isCompany) {
          key = buyer.orgnr;
        } else {
          key = buyer.email;
        }
        if (!entityMap[key]) {
          buyer._id = uuid();
          entityMap[key] = buyer;
        } else {
          entityMap[key] = this.mergeEntityFields(entityMap[key], buyer);
        }
        eventDataCopy[index].buyerId = entityMap[key]._id?.toString();
      }

      if (seller.firstName || seller.lastName || seller.companyname) {
        let key: string;
        if (seller.isCompany) {
          key = seller.orgnr;
        } else {
          key = seller.email;
        }
        if (!entityMap[key]) {
          seller._id = uuid();
          entityMap[key] = seller;
        } else {
          entityMap[key] = this.mergeEntityFields(entityMap[key], seller);
        }
        eventDataCopy[index].sellerId = entityMap[key]._id?.toString();
      }
      index++;
    }

    return {
      entities: Object.keys(entityMap).map((key: string): Entity => {
        return entityMap[key];
      }),
      eventDataWithEntityIds: eventDataCopy,
    };
  }

  private static mergeEntityFields(prevEntity: Entity, newEntity: Entity): Entity {
    let retEntity = { ...prevEntity };
    if (newEntity.companyname && newEntity.isCompany) {
      retEntity.companyname = newEntity.companyname;
    }
    if (!newEntity.isCompany) {
      if (newEntity.firstName) {
        retEntity.firstName = newEntity.firstName;
      }
      if (newEntity.lastName) {
        retEntity.lastName = newEntity.lastName;
      }
    }
    if (newEntity.address) {
      retEntity.address = newEntity.address;
    }
    if (newEntity.city) {
      retEntity.city = newEntity.city;
    }
    if (newEntity.zip) {
      retEntity.zip = newEntity.zip;
    }
    if (newEntity.dob) {
      retEntity.dob = newEntity.dob;
    }
    if (newEntity.pnr) {
      retEntity.pnr = newEntity.pnr;
    }
    if (newEntity.firstName) {
      retEntity.firstName = newEntity.firstName;
    }
    if (newEntity.lastName) {
      retEntity.lastName = newEntity.lastName;
    }
    if (newEntity.tlf) {
      retEntity.tlf = newEntity.tlf;
    }
    if (newEntity.banknr) {
      retEntity.banknr = newEntity.banknr;
    }
    if (newEntity.email) {
      retEntity.email = newEntity.email;
    }
    if (newEntity.citizenship) {
      retEntity.citizenship = newEntity.citizenship;
    }
    if (newEntity.lei) {
      retEntity.lei = newEntity.lei;
    }
    if (newEntity.vps) {
      retEntity.vps = newEntity.vps;
    }
    if (!retEntity.accounttype && newEntity.accounttype) {
      retEntity.accounttype = newEntity.accounttype;
    }
    return retEntity;
  }

  private static parseEvents({
    eventData,
    organizationId,
  }: {
    eventData: GenericEventRow[];
    organizationId: string;
  }): ShareholderEvent[] {
    const eventBlobs = eventData.reduce((acc: any, row: GenericEventRow) => {
      const key = row.type + ' ' + row.date;
      if (row.type && row.date) {
        if (acc[key]) {
          acc[key].push(row);
        } else {
          acc[key] = [row];
        }
      }
      return acc;
    }, {});
    const problemCells = [];
    const events: ShareholderEvent[] = Object.keys(eventBlobs).flatMap((key: string): any => {
      const eventList = eventBlobs[key];

      const firstElement = eventList[0];
      const trimmedType: string = firstElement.type
        .toLowerCase()
        .trim()
        .replace(/ /g, '')
        .replace(/-/g, '')
        .replace(/\//g, '');

      let eventType: string = Object.keys(translatedType).reduce(
        (eventType: string, key: string) => {
          if (translatedType[key].find((ty: string) => ty === trimmedType)) {
            return key;
          }
          return eventType;
        },
        null
      );

      if (!eventType) {
        throw {
          code: EventsImportErrorCodes.NOT_VALID_TRANSACTION,
          message: firstElement.type || null,
        };
      }

      let subType;
      if (new Set<string>(Object.keys(specialTransactions)).has(eventType)) {
        subType = eventType;
        eventType = ShareholderEventType.transaction;
      }

      let date: Date;
      if (moment(firstElement.date, validDateFormats, true).isValid()) {
        date = moment(firstElement.date, validDateFormats).toDate();
      } else {
        throw {
          code: EventsImportErrorCodes.NOT_A_VALID_DATE,
          message: firstElement,
        };
      }

      const _id = uuid();

      if (eventType === ShareholderEventType.transaction) {
        if (+firstElement.price === 100000) {
          console.log('fe123f');
        }
        return eventList.map(
          (row: GenericEventRow): TransactionEvent => ({
            _id,
            type: ShareholderEventType.transaction,
            sender: row.sellerId,
            recipient: row.buyerId,
            amount: +row.amount,
            price: +row.price || null,
            organizationId,
            shareclassId: row.shareclassId,
            subType: subType,
            creator: null,
            date,
          })
        );
      } else if (eventType === ShareholderEventType.emission) {
        const emission: EmissionEvent = {
          _id,
          type: ShareholderEventType.emission,
          organizationId,
          creator: null,
          date,
          price: +firstElement.price || null,
          state: {
            records: eventList.map(
              (row: GenericEventRow): ShareholderRecord => ({
                entityId: row.buyerId,
                shareclassId: row.shareclassId,
                amount: +row.amount,
                notes: row.notes,
              })
            ),
          },
        };
        return emission;
      } else if (eventType === ShareholderEventType.establish) {
        const establish: EstablishEvent = {
          _id,
          type: ShareholderEventType.establish,
          organizationId,
          creator: null,
          date,
          price: +firstElement.price || null,
          state: {
            records: eventList.map(
              (row: GenericEventRow): ShareholderRecord => ({
                entityId: row.buyerId,
                shareclassId: row.shareclassId,
                amount: +row.amount,
                notes: row.notes,
              })
            ),
          },
        };
        return establish;
      } else if (eventType === ShareholderEventType.import) {
        const importEvent: ImportEvent = {
          _id,
          type: ShareholderEventType.import,
          organizationId,
          creator: null,
          date,
          price: +firstElement.price || null,
          state: {
            records: eventList.map(
              (row: GenericEventRow): ShareholderRecord => ({
                entityId: row.buyerId,
                shareclassId: row.shareclassId,
                amount: +row.amount,
                notes: row.notes,
              })
            ),
          },
        };
        return importEvent;
      } else if (eventType === ShareholderEventType.split) {
        const splitEvent: SplitEvent = {
          _id,
          type: ShareholderEventType.split,
          organizationId,
          creator: null,
          date,
          from: +firstElement.from,
          to: +firstElement.to,
          strategy: translatedRoundingStrategies[firstElement.strategy.toLowerCase()],
          shareclassId: firstElement.shareclassId,
        };
        return splitEvent;
      } else if (eventType === ShareholderEventType.splice) {
        const spliceEvent: SpliceEvent = {
          _id,
          type: ShareholderEventType.splice,
          organizationId,
          creator: null,
          date,
          from: +firstElement.from,
          to: +firstElement.to,
          strategy: translatedRoundingStrategies[firstElement.strategy.toLowerCase()],
          shareclassId: firstElement.shareclassId,
        };
        return spliceEvent;
      } else if (eventType === ShareholderEventType.sharedelete) {
        const shareDeleteEvent: ShareDeletionDataEvent = {
          _id,
          type: ShareholderEventType.sharedelete,
          organizationId,
          creator: null,
          date,
          sender: firstElement.sellerId,
          amount: +firstElement.amount,
          shareclassId: firstElement.shareclassId,
          subType: subType,
        };
        return shareDeleteEvent;
      } else if (eventType === ShareholderEventType.dividend) {
        const dividendEvent: DividendEvent = {
          _id,
          type: ShareholderEventType.dividend,
          organizationId,
          creator: null,
          date,

          dividend: firstElement.price,
          paymentDate: firstElement.dividendpaymentdate,
          shareclassId: firstElement.shareclassId,
          state: {
            records: eventList.map(
              (row: GenericEventRow): ShareholderRecord => ({
                entityId: row.buyerId,
                shareclassId: row.shareclassId,
                amount: +row.amount,
                notes: row.notes,
              })
            ),
          },
        };
        //remove the first element from the records list, as it is the header

        dividendEvent.state.records = dividendEvent.state.records.slice(1);

        return dividendEvent;
      } else if (eventType === ShareholderEventType.parvaluechange) {
        const parValueChangeEvent: ShareParvalueChangeEvent = {
          _id,
          type: ShareholderEventType.parvaluechange,
          organizationId,
          creator: null,
          date,
          shareclassId: firstElement.shareclassId,
          parValue: +firstElement.price || 0,
        };
        return parValueChangeEvent;
      }
    });

    if (problemCells.length >= 1) {
      throw {
        code: 'EVENTS_PROBLEMS',
        message: problemCells,
      };
    }
    return events;
  }
  private static checkFieldValues(eventTable, headers, headerIndex) {
    const problemCell = [];
    let typeIndexed = -1;

    for (let i = headerIndex + 1; i < eventTable.length; i++) {
      const eventTableRow = eventTable[i];
      for (let x = 0; x < headers.length; x++) {
        if (headers[x] === 'type' && typeIndexed < 0) {
          typeIndexed = x;
        }

        const trimmedType: string = eventTableRow[typeIndexed]
          .toLowerCase()
          .trim()
          .replace(/ /g, '')
          .replace(/-/g, '')
          .replace(/\//g, '');
        const eventType: string = Object.keys(translatedType).reduce(
          (eventType: string, key: string) => {
            if (translatedType[key].find((ty: string) => ty === trimmedType)) {
              return key;
            }
            return eventType;
          },
          null
        );

        const problem = {
          header: eventTable[headerIndex][x],
          row: i + 1,
          eventType: eventTableRow[typeIndexed] || '',
          errorReason: ErrorCodes.NOTDEFINED,
        };

        const isValidEmail =
          /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

        switch (eventType) {
          case 'splice':
          case 'split':
            if (headers[x] === 'from' || headers[x] === 'to') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              } else if (isNaN(eventTableRow[x])) {
                problem.errorReason = ErrorCodes.NOTANUMBER;

                problemCell.push(problem);
              }
            }
            if (headers[x] === 'strategy') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              }
            }
            break;
          case 'establish':
          case 'emission':
          case 'import':
            if (headers[x] === 'buyer') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              }
            }
            if (headers[x] === 'buyeremail') {
              if (!eventTableRow[x]) {
                break;
              } else if (!isValidEmail.test(eventTableRow[x].toLowerCase().trim())) {
                problem.errorReason = ErrorCodes.NOTAEMAIL;
                problemCell.push(problem);
              }
            }
            break;
          case 'sharedelete':
            if (headers[x] === 'seller') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              }
            }
            if (headers[x] === 'selleremail') {
              if (!eventTableRow[x]) {
                break;
              } else if (!isValidEmail.test(eventTableRow[x].toLowerCase().trim())) {
                problem.errorReason = ErrorCodes.NOTAEMAIL;

                problemCell.push(problem);
              }
            }
            break;
          case 'dividend':
            if (headers[x] === 'price') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              } else if (isNaN(eventTableRow[x])) {
                problem.errorReason = ErrorCodes.NOTANUMBER;

                problemCell.push(problem);
              }
            }
            break;
          case 'parvaluechange':
            if (headers[x] === 'price') {
              if (!eventTableRow[x]) {
                problem.errorReason = ErrorCodes.EMPTYROW;
                problemCell.push(problem);
              } else if (isNaN(eventTableRow[x])) {
                problem.errorReason = ErrorCodes.NOTANUMBER;

                problemCell.push(problem);
              }
            }
            break;
          default:
            //removed this to make dividends work, not sure if it is a mistake or not, but atleast it somewhat works
            // if (headers[x] === 'buyer' || headers[x] === 'seller') {
            //   if (!eventTableRow[x]) {
            //     problem.errorReason = ErrorCodes.EMPTYROW;
            //     problemCell.push(problem);
            //   }
            // }
            break;
        }
        if (headers[x] === 'type') {
          if (!eventTableRow[x]) {
            problem.errorReason = ErrorCodes.EMPTYROW;
            problem.eventType = '';
            problemCell.push(problem);
          }
        }
        if (headers[x] === 'date') {
          if (!eventTableRow[x]) {
            problem.errorReason = ErrorCodes.EMPTYROW;
            problemCell.push(problem);
          }
        }
      }
    }
    if (problemCell.length >= 1) {
      throw {
        code: 'EVENTS_PROBLEMS',
        message: problemCell,
      };
    }
  }
  private static CheckEventValidity(events: GenericEventRow[]) {
    const problemCell = [];
    const companyentities = [];
    const privatepersonsentities = [];
    for (const event of events) {
      const problem = {
        header: '',
        row: 0,
        eventType: event.type || '',
        errorReason: ErrorCodes.NOTDEFINED,
      };
      const trimmedType: string = event.type
        .toLowerCase()
        .trim()
        .replace(/ /g, '')
        .replace(/-/g, '')
        .replace(/\//g, '');
      const eventType: string = Object.keys(translatedType).reduce(
        (eventType: string, key: string) => {
          if (translatedType[key].find((ty: string) => ty === trimmedType)) {
            return key;
          }
          return eventType;
        },
        null
      );
      if (
        eventType === 'import' ||
        eventType === 'emission' ||
        eventType === 'establish' ||
        eventType === 'transaction' ||
        eventType === 'sale'
      ) {
        const isBuyerCompany = this.isCompany(event.buyerinvestorid);

        const buyer: Entity = this.parseEntity({
          isCompany: isBuyerCompany,
          investorid: event.buyerinvestorid,
          name: event.buyer,
          contactName: event.buyercontactpersoncompany,
          email: event.buyeremail,
          address: event.buyeraddress,
          zip: event.buyerzip,
          city: event.buyercity,
          citizenship: event.buyercountry,
          tlf: event.buyertlf,
          organizationId: '',
          lei: event.buyerlei,
          vps: event.buyervps,
          accounttype: event.accounttype,
        });

        if (isBuyerCompany) {
          // write a check for company entities and if they are valid or not a company is the only one that can have the same ID and name,
          // if the name is not the same then it is a different company but need to trow an error if the name is the same but the ID is not
          const res = companyentities.find((entity: Entity) => {
            if (entity.orgnr === buyer.orgnr) {
              if (entity.companyname.toLowerCase() !== buyer.companyname.toLowerCase()) {
                problem.header = event.date + ' org Id:' + buyer.orgnr;
                problem.errorReason = ErrorCodes.NOTALLOWEDFORENTITY;
                problemCell.push(problem);
                return;
              }
              return true;
            }
            return false;
          });
          if (!res) {
            companyentities.push(buyer);
          }
        } else {
          const res = privatepersonsentities.find((entity: Entity) => {
            if (entity.dob === buyer.dob && entity.email === buyer.email) {
              if (entity.firstName === buyer.firstName && entity.lastName === buyer.lastName) {
                //   problem.errorReason = ErrorCodes.EMPTYROW;
                //    problemCell.push(problem);
                return true;
              }
            }
            return false;
          });
          if (!res) {
            privatepersonsentities.push(buyer);
          }
        }
      }
    }

    if (problemCell.length >= 1) {
      throw {
        code: 'EVENTS_PROBLEMS',
        message: problemCell,
      };
    }
  }
  //write a check to see if the entity is hapening in the same event, if it is then it is a problem unless the shareclass is different.
}
